import { useState, type MouseEvent } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, type SxProps, type Theme } from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import { useColorMode, type ColorModePreference } from '@/contexts/ColorModeContext';

type Props = {
  color?: 'inherit' | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  size?: 'small' | 'medium' | 'large';
  sx?: SxProps<Theme>;
};

export default function ColorModeMenuButton({ color = 'inherit', size = 'medium', sx }: Props) {
  const { preference, setColorMode } = useColorMode();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);

  const pick = (m: ColorModePreference) => {
    setColorMode(m);
    setAnchor(null);
  };

  const currentIcon =
    preference === 'light' ? (
      <LightModeIcon />
    ) : preference === 'dark' ? (
      <DarkModeIcon />
    ) : (
      <SettingsBrightnessIcon />
    );

  return (
    <>
      <Tooltip title="顯示模式（淺色／深色／自動）">
        <IconButton
          color={color}
          size={size}
          sx={sx}
          onClick={(e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
          aria-label="顯示模式"
          aria-haspopup="true"
          aria-expanded={open}
        >
          {currentIcon}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => pick('light')} selected={preference === 'light'}>
          <ListItemIcon>
            <LightModeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="淺色" />
        </MenuItem>
        <MenuItem onClick={() => pick('dark')} selected={preference === 'dark'}>
          <ListItemIcon>
            <DarkModeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="深色" />
        </MenuItem>
        <MenuItem onClick={() => pick('system')} selected={preference === 'system'}>
          <ListItemIcon>
            <SettingsBrightnessIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="自動" secondary="跟隨系統" />
        </MenuItem>
      </Menu>
    </>
  );
}
